import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ToastProps {
    message: string;
    type: 'success' | 'error' | 'info';
    onDismiss: () => void;
    /** Bottom offset in px — use when stacking two toasts */
    bottomOffset?: number;
}

const TOAST_STYLES = {
    success: {
        border: '#CDE8E2',
        iconBg: '#EEF8F5',
        icon: (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0B6B5F" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        ),
    },
    error: {
        border: '#F3D3D9',
        iconBg: '#FBE7EA',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B23A4E" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        ),
    },
    info: {
        border: '#CDD9F0',
        iconBg: '#E9EEF9',
        icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B5BA9" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></svg>
        ),
    },
} as const;

export const Toast: React.FC<ToastProps> = ({ message, type, onDismiss, bottomOffset = 24 }) => {
    useEffect(() => {
        if (type === 'success' || type === 'info') {
            const t = setTimeout(onDismiss, 5000);
            return () => clearTimeout(t);
        }
    }, [type, onDismiss]);

    const style = TOAST_STYLES[type];

    return createPortal(
        <div
            style={{ bottom: bottomOffset, borderColor: style.border }}
            role="alert"
            className="mapper-ui font-sans fixed right-6 z-[10000] max-w-[420px] min-w-[280px] flex items-start gap-3 px-4 py-[14px] rounded-2xl bg-mapper-white shadow-mapper-modal border"
        >
            <div
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: style.iconBg }}
            >
                {style.icon}
            </div>
            <span className="flex-1 text-[13.5px] font-medium text-mapper-text pt-[3px]">{message}</span>
            <button
                onClick={onDismiss}
                aria-label="Dismiss"
                className="shrink-0 text-mapper-muted-3 text-lg leading-none px-1"
            >
                ×
            </button>
        </div>,
        document.body
    );
};
